import React, { useState, useEffect, useRef } from 'react';
import { Table, Row, Col, Breadcrumb, Button, Typography, List, notification, Space, Grid, Tooltip } from 'antd';
import { CSVLink } from 'react-csv';
import { ADDITIONAL_COL } from '../../../config';
import {
  intialPaginationState,
  showingPageTotalRange,
  filter,
  heightWidth,
  onlyYears,
  PictureCell,
  stampSize,
  unexpectedError,
} from '../../../common_functions';
import { DownloadOutlined, ReloadOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import ImageAdvanceFilter from './ImageAdvanceFilter';
import CollectionLocationsForm from '../common-comp/CollectionLocationsForm';
import AdditionalColModal from './AdditionalColModal';

const defaultCheckedList = ADDITIONAL_COL.filter(col => col.checked).map(col => col.key);

const ImageReport = props => {
  const collectionFormRef = useRef(null);
  const [reports, setReports] = useState([]);
  const [sorter, setSorter] = useState(null);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [pagination, setPagination] = useState(intialPaginationState);
  const [loading, setLoading] = useState(false);
  const [checkedList, setCheckedList] = useState(defaultCheckedList);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [resetFilterData, setResetFilterData] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isResetting, setIsResetting] = useState(false);
  const [isModal2Visible, setModal2Visible] = useState(false);
  const [tempCheckedList, setTempCheckedList] = useState(defaultCheckedList);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  // Confidence "value" is normally a short score (e.g. "99"); some records carry long
  // placeholder/garbage text in that same field, so fall back to truncated text + tooltip.
  const ValueBadge = ({ value }) => {
    if (value == null || value === '') return <span style={{ color: '#bfbfbf' }}>—</span>;
    if (String(value).length > 8) {
      return (
        <Tooltip title={value} placement="topLeft">
          <span
            style={{
              display: 'inline-block',
              maxWidth: 140,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'bottom',
              color: '#595959',
              cursor: 'help',
            }}
          >
            {value}
          </span>
        </Tooltip>
      );
    }
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: 12,
          background: '#f7f7e8',
          border: '1px solid #d9d68f',
          color: '#7a7800',
          fontWeight: 600,
          fontSize: 13,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await getDataReports(intialPaginationState);
      setLoading(false);
    };
    fetchData();
  }, []);
  const transformReportData = rawData => {
    return rawData.map(item => {
      const print = item.print || {};
      const xrf = print.xrf?.[0] || {};
      const glossmeter = print.glossmeter?.[0] || {};
      const photoSpectroMeter = print.photo_spectro_meter?.[0] || {};
      const micrometer = print.micrometer?.[0] || {};
      const basicPhotos = print.basic_photos?.[0] || {};

      return {
        id: item.id,
        title: item.title,
        acquisition: item.acquisition,
        collections: item.collections?.name,
        location: item?.location?.name,
        authenticity_confidences: item.authenticity_confidences?.value,
        print_common_tile: print?.object_name,
        print_date_range: onlyYears(print?.creation_date_start_year, print?.creation_date_end_year),
        negative_id: print?.negative_id?.obj_name,
        negative_thumbnail: print?.negative_id?.thumbnail,
        neg_date_range: onlyYears(print?.negative_id?.creation_earliest, print?.negative_id?.creation_latest),
        portfolio_info: print?.portfolio_add_number?.name
          ? `${print.portfolio_add_number.name}${print.edition_set?.edition_number ? `-${print.edition_set.edition_number}` : ''}`
          : '',
        date_confidence: print?.date_confidence || [],
        image_size: heightWidth(print?.image_dimension_width, print?.image_dimension_height),
        stamp_name: print?.stamp?.stamp_name,
        stamp_size: stampSize(print?.name_line_width, print?.city_line_width, print?.stamp_dimension_height),
        standard_stamp_size: stampSize(print?.standard_name_line_width, print?.standard_city_line_width, print?.standard_stamp_height),
        //Glossmeter
        gloss: glossmeter?.paper_sheen,
        //micrometer data
        mount_thickness: micrometer?.thickness,
        paper_weight: micrometer?.paper_weight,

        // XRF data
        avg_dmin_ba_sr: xrf.avg_dmin_ba_sr,
        avg_dmin_ba: xrf.avg_dmin_ba,
        avg_dmin_sr: xrf.avg_dmin_sr,
        presence_of_selenium_se: xrf.presence_of_selenium_se,

        //photospectrometer data
        oba: photoSpectroMeter?.oba,
        // Annotations
        annotations_recto: basicPhotos?.annotations_recto ?? [],
        annotations_verso: basicPhotos?.annotations_verso ?? [],
        stamp_verso: basicPhotos.stamp_verso ?? [],
        image_detail: basicPhotos?.image_detail ?? [],
        signature_recto: basicPhotos?.signature_recto,
        signature_verso: basicPhotos?.signature_verso,
        signature_recto_text: basicPhotos?.signature_recto_text?.signature,
        signature_verso_text: basicPhotos?.signature_verso_text?.signature,
      };
    });
  };
  const getDataReports = async (pagination, filters = { object_type: { _eq: 'print' } }, sorter = '') => {
    const isAdmin = props.roleType === 'admin';
    let location = collectionFormRef?.current?.getFieldValue('locations');
    let collections = collectionFormRef?.current?.getFieldValue('collections');
    if (!isAdmin) {
      filters = { ...filters, collections: { _in: props.collections_permissions.view } };
    }
    if (location) {
      filters = { ...filters, location: { _eq: location } };
    }
    if (collections) {
      filters = { ...filters, collections: { _eq: collections } };
    }
    try {
      setLoading(true);
      const response = await props.getItems('collection_obj', {
        fields: [
          'id,title, acquisition,collections.name,collections.id,print.object_name',
          'print.creation_date_start_year,print.creation_date_end_year,print.photo_spectro_meter.oba,location.name',
          'print.date_confidence.date_confidence_id.value',
          'print.date_confidence.date_confidence_id.date_confidence',
          'print.negative_id.obj_name',
          'print.negative_id.creation_earliest',
          'print.negative_id.creation_latest',
          'print.negative_id.thumbnail',
          'print.xrf.avg_dmin_ba_sr',
          'print.portfolio_add_number.name',
          'print.edition_set.edition_number',
          'print.stamp.stamp_name',
          'authenticity_confidences.value',
          'print.object_name',
          'print.basic_photos.annotations_verso.directus_files_id',
          'print.basic_photos.annotations_verso.hand_writing_notes.name',
          'print.basic_photos.annotations_verso.hand_writing_notes.begin_date',
          'print.basic_photos.annotations_verso.hand_writing_notes.end_date',
          'print.basic_photos.annotations_recto.directus_files_id.id',
          'print.basic_photos.annotations_recto.directus_files_id.filename_download',
          'print.basic_photos.annotations_recto.hand_writing_notes.name',
          'print.basic_photos.annotations_recto.hand_writing_notes.begin_date',
          'print.basic_photos.annotations_recto.hand_writing_notes.end_date',
          'print.basic_photos.stamp_verso.hand_writing_notes.name',
          'print.basic_photos.stamp_verso.hand_writing_notes.begin_date',
          'print.basic_photos.stamp_verso.hand_writing_notes.end_date',
          'print.basic_photos.stamp_verso.directus_files_id',
          'print.basic_photos.image_detail.*',
          'print.basic_photos.signature_verso',
          'print.basic_photos.signature_recto',
          'print.basic_photos.signature_recto_text.signature',
          'print.basic_photos.signature_verso_text.signature',
          'print.image_dimension_width,print.image_dimension_height,print.stamp.width, print.stamp.height',
          'print.standard_stamp_height,print.standard_name_line_width,print.standard_city_line_width',
          'print.city_line_width',
          'print.name_line_width',
          'print.stamp_dimension_width',
          'print.stamp_dimension_height',
        ],
        filters: filters,
        sort: sorter && sorter.field ? `${sorter.order === 'ascend' ? '' : '-'}${sorter.field}` : '-id',
        offset: pagination.pageSize * (pagination.current - 1),
        limit: pagination.pageSize,
        meta: 'filter_count',
      });
      setReports(transformReportData(response.data));
      pagination.total = response.meta.filter_count;
      setPagination(pagination);
    } catch (error) {
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: true,
    onChange: (newSelectedRowKeys, newSelectedRows) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(newSelectedRows);
    },
  };

  const DEFAULT_COLUMNS = [
    {
      title: 'Acquisition',
      dataIndex: 'acquisition',
      key: 'acquisition',
      width: 100,
      ellipsis: true,
      ...filter('acquisition'),
    },
    {
      title: 'Collection',
      dataIndex: 'collections',
      key: 'collections',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Signature Recto',
      dataIndex: 'signature_recto',
      key: 'signature_recto',
      align: 'center',
      width: 200,
      render: signature_recto =>
        signature_recto ? <PictureCell dataIndexValue={signature_recto} getFile={props.getFile} width={200} /> : <span>-</span>,
    },
    {
      title: 'Signature Verso',
      dataIndex: 'signature_verso',
      key: 'signature_verso',
      align: 'center',
      width: 200,
      render: signature_verso =>
        signature_verso ? <PictureCell dataIndexValue={signature_verso} getFile={props.getFile} width={200} /> : <span>-</span>,
    },
    {
      title: 'Stamp Verso',
      dataIndex: 'stamp_verso',
      key: 'stamp_verso',
      align: 'center',
      width: 100,
      render: stampVerso => {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '30px' }}>
            {stampVerso.map((item, index) =>
              item?.directus_files_id ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '30px' }}>
                    <PictureCell
                      key={item.directus_files_id || index}
                      dataIndexValue={item.directus_files_id}
                      getFile={props.getFile}
                      width={200}
                    />
                    <span style={{ width: 200 }}>Stamp Verso{index + 1}</span>
                  </div>
                </>
              ) : (
                ''
              )
            )}
          </div>
        );
      },
    },
    {
      title: 'Annotations Recto',
      dataIndex: 'annotations_recto',
      key: 'annotations_recto',
      align: 'center',
      width: 100,
      render: annotationRecto => {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '30px' }}>
            {annotationRecto.map((item, index) =>
              item?.directus_files_id?.id ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '30px' }}>
                    <PictureCell
                      key={item.directus_files_id?.id || index}
                      dataIndexValue={item.directus_files_id?.id}
                      getFile={props.getFile}
                      width={200}
                    />
                    <span style={{ width: 200 }}>Annnotations Recto {index + 1}</span>
                  </div>
                </>
              ) : (
                <span key={index}>-</span>
              )
            )}
          </div>
        );
      },
    },

    {
      title: 'Annotations Verso',
      dataIndex: 'annotations_verso',
      key: 'annotations_verso',
      align: 'center',
      width: 200,
      render: annotationVerso => {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '30px' }}>
            {annotationVerso.map((item, index) =>
              item?.directus_files_id ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '30px' }}>
                  <PictureCell
                    key={item.directus_files_id || index}
                    dataIndexValue={item.directus_files_id}
                    getFile={props.getFile}
                    width={200}
                  />
                  <span style={{ width: 200 }}>Annotations Verso {index + 1}</span>
                </div>
              ) : (
                ''
              )
            )}
          </div>
        );
      },
    },
    {
      title: 'Image Detail',
      dataIndex: 'image_detail',
      key: 'image_detail',
      align: 'center',
      width: 200,
      render: imageDetail => {
        if (!imageDetail.length) {
          return <span>-</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
            {imageDetail.map((item, index) =>
              item?.directus_files_id ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: '30px' }}>
                  <PictureCell
                    key={item.directus_files_id || index}
                    dataIndexValue={item.directus_files_id}
                    getFile={props.getFile}
                    width={200}
                  />
                  <span style={{ width: 200 }}>Image Detail {index + 1}</span>
                </div>
              ) : (
                <span key={index}>-</span>
              )
            )}
          </div>
        );
      },
    },

    {
      title: 'Print Date Range',
      dataIndex: 'print_date_range',
      key: 'print_date_range',
    },
    {
      title: 'OBA',
      dataIndex: 'oba',
      key: 'oba',
    },
  ];
  const allColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'id' ? sortedInfo.order : null,
      ellipsis: true,
    },
    ...DEFAULT_COLUMNS,

    {
      title: 'Print Common Title',
      dataIndex: 'print_common_tile',
      key: 'print_common_tile',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Negative/image',
      dataIndex: 'negative_id',
      key: 'negative_id',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Negative Thumbnail',
      dataIndex: 'negative_thumbnail',
      key: 'negative_thumbnail',
      width: 220,
      render: negative_thumbnail =>
        negative_thumbnail ? <PictureCell dataIndexValue={negative_thumbnail} getFile={props.getFile} width={200} /> : <span>-</span>,
    },
    {
      title: 'Negative Date',
      dataIndex: 'neg_date_range',
      key: 'neg_date_range',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Portfolio',
      dataIndex: 'portfolio_info',
      key: 'portfolio_info',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Authenticity Value',
      dataIndex: 'authenticity_confidences',
      key: 'authenticity_confidences',
      width: 150,
      render: value => <ValueBadge value={value} />,
    },
    {
      title: 'Date Confidence Value',
      dataIndex: 'date_confidence',
      key: 'date_confidence',
      width: 220,
      render: dateConfidence =>
        dateConfidence?.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dateConfidence.map((item, idx) => (
              <div key={idx} style={{ borderLeft: '3px solid #a6a300', paddingLeft: 8 }}>
                <strong style={{ color: '#212329', marginRight: 6 }}>{item?.date_confidence_id?.date_confidence}</strong>
                <ValueBadge value={item?.date_confidence_id?.value} />
              </div>
            ))}
          </div>
        ) : (
          <span style={{ color: '#bfbfbf' }}>—</span>
        ),
    },
    {
      title: 'Ba/Sr',
      dataIndex: 'avg_dmin_ba_sr',
      key: 'avg_dmin_ba_sr',
    },
    {
      title: 'Signature Recto Text',
      dataIndex: 'signature_recto_text',
      key: 'signature_recto_text',
    },
    {
      title: 'Signature Verso Text',
      dataIndex: 'signature_verso_text',
      key: 'signature_verso_text',
    },
    {
      title: 'Annotations Recto Handwriting',
      dataIndex: 'annotations_recto_handwriting',
      key: 'annotations_recto_handwriting',
      render: (text, record) => {
        const annotationRecto = record?.annotations_recto;
        return (
          <>
            <List
              dataSource={annotationRecto}
              renderItem={item =>
                item?.hand_writing_notes && (
                  <List.Item style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px', borderBottom: 'none' }}>
                    <strong>{item?.hand_writing_notes?.name} : </strong>
                    {onlyYears(item?.hand_writing_notes?.begin_date, item?.hand_writing_notes?.end_date)}
                  </List.Item>
                )
              }
            />
          </>
        );
      },
    },
    {
      title: 'Annotations Verso Handwriting',
      dataIndex: 'annotations_verso_handwriting',
      key: 'annotations_verso_handwriting',
      render: (text, record) => {
        const annotationVerso = record?.annotations_verso;
        return (
          <>
            <List
              dataSource={annotationVerso}
              renderItem={item =>
                item?.hand_writing_notes && (
                  <List.Item style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px', borderBottom: 'none' }}>
                    <strong>{item?.hand_writing_notes?.name} : </strong>

                    {onlyYears(item?.hand_writing_notes?.begin_date, item?.hand_writing_notes?.end_date)}
                  </List.Item>
                )
              }
            />
          </>
        );
      },
    },
    {
      title: 'Stamp Verso Handwriting',
      dataIndex: 'stamp_verso_handwriting',
      key: 'stamp_verso_handwriting',
      render: (text, record) => {
        const stampVerso = record?.stamp_verso;
        return (
          <>
            <List
              dataSource={stampVerso}
              renderItem={item =>
                item?.hand_writing_notes && (
                  <List.Item style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px', borderBottom: 'none' }}>
                    <strong>{item?.hand_writing_notes?.name} : </strong>
                    {onlyYears(item?.hand_writing_notes?.begin_date, item?.hand_writing_notes?.end_date)}
                  </List.Item>
                )
              }
            />
          </>
        );
      },
    },
    {
      title: 'Image Size (w X h)',
      dataIndex: 'image_size',
      key: 'image_size',
    },
    {
      title: 'Stamp Name',
      dataIndex: 'stamp_name',
      key: 'stamp_name',
    },
    {
      title: 'Standard Stamp Size (Name X City X Height)',
      dataIndex: 'standard_stamp_size',
      key: 'standard_stamp_size',
    },
    {
      title: 'Stamp Size (Name X City X Height)',
      dataIndex: 'stamp_size',
      key: 'stamp_size',
    },
  ];

  const newColumns = allColumns
    .filter(col => DEFAULT_COLUMNS.some(defaultCol => defaultCol.key === col.key) || checkedList.includes(col.key))
    .map(col => ({ width: 150, ...col }));
  const options = ADDITIONAL_COL.map(({ key, value }) => ({
    label: value,
    value: key,
  }));
  const handleClose = () => {
    setIsModalVisible(false);
  };
  const showModal = () => {
    setIsModalVisible(true);
  };

  const excludedColumns = [
    'negative_thumbnail',
    'stamp_verso',
    'signature_verso',
    'signature_recto',
    'image_detail',
    'stamp_verso',
    'annotations_recto',
    'annotations_verso',
  ];
  const filteredColumns = newColumns.filter(col => !excludedColumns.includes(col.dataIndex));
  const csvHeaders = filteredColumns.map(col => ({ label: col.title, key: col.dataIndex }));

  const csvData = selectedRows.map(row => {
    const formattedRow = {};

    newColumns.forEach(col => {
      if (excludedColumns.includes(col.dataIndex)) {
        return;
      }

      let value = row[col.dataIndex];

      if (col.render) {
        value = col.render(null, row);
      }

      // Handle specific nested cases
      switch (col.dataIndex) {
        case 'authenticity_confidences':
          value = row[col.dataIndex] ?? '';
          break;

        case 'date_confidence':
          value = row?.date_confidence
            ?.map(item => `${item?.date_confidence_id?.date_confidence}: ${item?.date_confidence_id?.value}`)
            .filter(Boolean)
            .join('\n');
          break;

        case 'stamp_verso_handwriting':
          value = row?.stamp_verso
            ?.map(item =>
              item?.hand_writing_notes
                ? `${item.hand_writing_notes.name}: ${onlyYears(item.hand_writing_notes.begin_date, item.hand_writing_notes.end_date)}`
                : ''
            )
            .filter(Boolean)
            .join('\n');
          break;

        case 'annotations_verso_handwriting':
        case 'annotations_recto_handwriting':
          const field = col.dataIndex === 'annotations_verso_handwriting' ? 'annotations_verso' : 'annotations_recto';
          value = row?.[field]
            ?.map(item =>
              item?.hand_writing_notes
                ? `${item.hand_writing_notes.name}: ${onlyYears(item.hand_writing_notes.begin_date, item.hand_writing_notes.end_date)}`
                : ''
            )
            .filter(Boolean)
            .join('\n');
          break;

        default:
          value = value ?? '';
      }

      formattedRow[col.dataIndex] = value;
    });

    return formattedRow;
  });

  let handleTableChange = async (pagination, filters, sorter = '') => {
    sorter = sorter?.order == null ? '' : sorter;
    setPagination(pagination);
    setSorter(sorter);
    let filtersitems = { object_type: { _eq: 'print' } };
    for (let key in filters) {
      if (filters.hasOwnProperty(key)) {
        let val = filters[key];
        if (val) {
          filtersitems[key] = { _icontains: val[0] };
        }
      }
    }
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    await getDataReports(pagination, filtersitems, sorter);
  };
  const exportImages = async type => {
    if (selectedRowKeys.length > 0) {
      const data = {
        object_ids: selectedRowKeys,
      };
      setLoading(true);
      props.analyticalReports(data, type);
      notification['success']({
        message:
          'We will notify you via email upon completion of the report generation. You can view the report in the Report History tab.',
        duration: 2,
      });
      setSelectedRows([]);
      setSelectedRowKeys([]);
      setLoading(false);
    } else {
      notification.info({
        message: 'No data has been selected!',
        duration: 2,
      });
    }
  };

  useEffect(() => {
    (async () => {
    if (isResetting && reports.length === 0) {
      await getDataReports(intialPaginationState);
      setIsResetting(false);
    }
  })();
  }, [reports, isResetting]);

  const onFinish = () => {
    setReports([]);
    setPagination(intialPaginationState);
    setIsResetting(true);
  };

  const resetAdvanceImageSerachFilters = async () => {
    try {
      collectionFormRef.current.resetFields();
      await onFinish();
      setResetFilterData(true);
      notification.success({ message: 'Search filters are reset successfully!', duration: 2 });
    } catch (error) {
      unexpectedError(error);
    }
  };
  const showModal_2 = () => {
    setTempCheckedList(checkedList);
    setModal2Visible(true);
  };
  return (
    <Row className={!!loading ? 'disabledComponent' : ''}>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Analytical", href: "/reports/analytical-report/data-report" }, { title: "Image Report" }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <div className="dashboard">
          <Row gutter={[24, 12]} className="page-header-section report-buttons-row" align="middle">
            <Col xs={24} md={6}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                Image Report Result
              </Typography.Title>
            </Col>
            <Col xs={24} md={18} style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <Space wrap size={[8, 8]}>
                <Button icon={<PlusOutlined />} type="primary" size="small" onClick={showModal_2}>
                  Add Columns
                </Button>

                <Button icon={<SearchOutlined />} type="primary" size="small" onClick={showModal}>
                  Image Search
                </Button>
                <Button size="small" danger onClick={resetAdvanceImageSerachFilters} icon={<ReloadOutlined />}>
                  Reset Filters
                </Button>
                <CSVLink data={csvData} headers={csvHeaders} filename="analytical-image-report.csv">
                  <Button type="primary" disabled={selectedRows.length === 0} icon={<DownloadOutlined />} size="small">
                    Export CSV
                  </Button>
                </CSVLink>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    exportImages('generate-images-report');
                  }}
                  icon={<DownloadOutlined />}
                >
                  Export All Images
                </Button>
              </Space>
            </Col>
          </Row>
          <CollectionLocationsForm props={props} onFinish={onFinish} formRef={collectionFormRef} resetFilterData={resetFilterData} />
          <div className="artist-list image-report-table" style={{ marginTop: '20px' }}>
            <Table
              className="no-fixed-last-col"
              columns={newColumns}
              rowSelection={rowSelection}
              scroll={{ x: 'max-content' }}
              sticky
              size={isMobile ? 'small' : 'middle'}
              bordered
              rowKey={record => record.id}
              dataSource={reports}
              rowClassName={(_, index) => (index % 2 === 1 ? 'image-report-row-alt' : '')}
              pagination={
                reports.length > 0
                  ? { ...pagination, ...showingPageTotalRange, simple: isMobile, showSizeChanger: !isMobile }
                  : false
              }
              loading={loading}
              onChange={handleTableChange}
            />
            <style>{`
              .image-report-table .ant-table-tbody > tr.image-report-row-alt > td { background: #faf9f2 !important; }
              .image-report-table .ant-table-thead > tr > th { border-bottom: 2px solid #a6a300; }
              .image-report-table .ant-table-tbody > tr:hover > td { background: #f7f7e8 !important; }
            `}</style>
          </div>
        </div>
        <ImageAdvanceFilter
          visible={isModalVisible}
          onClose={handleClose}
          props={props}
          resetDataState={setResetFilterData}
          resetData={resetFilterData}
          fetchReports={getDataReports}
        />

        <AdditionalColModal
          setModal2Visible={setModal2Visible}
          isModal2Visible={isModal2Visible}
          tempCheckedList={tempCheckedList}
          setTempCheckedList={setTempCheckedList}
          options={options}
          checkedList={checkedList}
          setCheckedList={setCheckedList}
          ADDITIONAL_COL={ADDITIONAL_COL}
        />
      </Col>
    </Row>
  );
};
export default ImageReport;
